<template>
    <v-container
        fluid
        class="pa-2 pr-0 pb-1"
        :style="$vuetify.display.mobile ? 'width: 100vw;' : ''"
    >
        <template v-if="!$vuetify.display.mobile">
            <v-window v-model="globalStep">
                <v-window-item :value="0">
                    <v-row no-gutters v-if="loading">
                        <v-col cols="12" class="d-flex justify-center align-center">
                            <v-progress-circular indeterminate color="primary" />
                        </v-col>
                    </v-row>
                    <template v-else>
                        <v-row no-gutters class="my-2">
                            <v-col cols="6">
                                <v-btn-back @click.stop="back()"> </v-btn-back>
                                <v-btn
                                    v-if="hasAlreadyDeclaredItems"
                                    size="small"
                                    class="ml-2"
                                    @click="confirmVatItemsAlreadyDeclaredDialog = true"
                                    >Eléments déjà déclarés</v-btn
                                >
                            </v-col>
                            <v-col cols="6" class="d-flex justify-end pr-2">
                                <v-btn
                                    v-if="!showSummaryPanel"
                                    class="mr-2"
                                    size="small"
                                    @click="showSummaryPanel = true"
                                    >Afficher le récapitulatif</v-btn
                                >
                                <v-btn
                                    :disabled="!needSave"
                                    class="mr-2"
                                    size="small"
                                    @click="save()"
                                    >Enregistrer Assistant TVA</v-btn
                                >
                                <v-btn
                                    rounded
                                    elevation="0"
                                    :disabled="nextIsDisabled"
                                    v-if="globalStep < 1"
                                    @click="nextPage()"
                                    size="small"
                                    >Suivant</v-btn
                                >
                            </v-col>
                        </v-row>
                        <v-row no-gutters>
                            <v-col :cols="showSummaryPanel ? 9 : 12" class="pr-2">
                                <div>
                                    <VATDeclarationDetailCardAssist
                                        :vatItems="assistVatItemsNotAlreadyDeclared"
                                        :endDate="endDate"
                                        :fromAssistVATComponent="true"
                                        :isGrossAmountExceeded="isGrossAmountExceeded"
                                        @delete-sub-or-added-item="deleteItem"
                                        @add-item="addItem"
                                    />
                                </div>
                                <div>
                                    <VATDeclarationDetailCardAuto
                                        :vatItems="autoVatItemsNotAlreadyDeclared"
                                        :endDate="endDate"
                                        :fromAutoVATComponent="false"
                                    >
                                    </VATDeclarationDetailCardAuto>
                                </div>
                            </v-col>
                            <v-col cols="3" v-if="showSummaryPanel">
                                <VATDeclarationSummaryCard
                                    :autoVatItems="autoVatItemsNotAlreadyDeclared"
                                    :assistVatItems="assistVatItemsNotAlreadyDeclared"
                                    :previousVatCredit="previousVatCredit"
                                    :noVat="noVat"
                                    :showSummaryPanel="showSummaryPanel"
                                    @show-summary-panel="showSummaryPanel = false"
                                >
                                </VATDeclarationSummaryCard>
                            </v-col>
                        </v-row>
                    </template>
                </v-window-item>
                <v-window-item :value="1">
                    <v-row no-gutters>
                        <v-col cols="12">
                            <DeclarationManager
                                v-if="globalStep === 1 && version === 'new'"
                                :propsCerfaNumber="cerfaNumber"
                                :propsStartDate="startDate"
                                :propsEndDate="endDate"
                                :assistVatMode="true"
                                :propsAssistVatItems="assistVatItemsNotAlreadyDeclared"
                                :propsAutoVatItems="
                                    autoVatItemsNotAlreadyDeclared.length
                                        ? autoVatItemsNotAlreadyDeclared
                                        : null
                                "
                                :propsPreviousVatCredit="previousVatCredit"
                                @back="globalStep--"
                                :id="declarationId || declarationIdAfterSave"
                            >
                            </DeclarationManager>
                        </v-col>
                    </v-row>
                </v-window-item>
            </v-window>
        </template>
        <oss-dialog
            v-model="confirmVatItemsAlreadyDeclaredDialog"
            @valid="vatDeclarationAlreadyDone()"
            title="Confirmation"
            validTitle="Confirmer"
        >
            <div style="width: 80vw">
                <v-row no-gutters class="d-flex justify-center">
                    <b>
                        &#xca;tes-vous sûr(e) que ces factures et/ou mouvements bancaires ont déjà
                        été déclarés ?
                    </b>
                </v-row>
                <v-row no-gutters class="d-flex justify-center">
                    <b>
                        Après confirmation, ces éléments ne seront plus proposés, définitivement.
                    </b>
                </v-row>
                <VATDeclarationDetailCardAssist
                    :vatItems="assistVatItemsAlreadyDeclared"
                    :componentForAlreadyDeclaredItems="true"
                    v-if="assistVatItemsAlreadyDeclared && assistVatItemsAlreadyDeclared.length"
                />
                <VATDeclarationDetailCardAuto
                    :vatItems="autoVatItemsAlreadyDeclared"
                    :componentForAlreadyDeclaredItems="true"
                    v-if="autoVatItemsAlreadyDeclared && autoVatItemsAlreadyDeclared.length"
                />
            </div>
        </oss-dialog>
    </v-container>
</template>

<script lang="ts">
import { Apollo } from "@/decorators";
import VAT_CALCULATION_ASSIST from "@/graphql/vatDeclaration/VATCalculationAssist.gql";
import COMPUTE_VAT_DECLARATION_AUTO_IN_ASSIST from "@/graphql/vatDeclaration/ComputeVatDeclarationAutoInAssist.gql";
import DeclarationForm from "./DeclarationForm.vue";
import DeclarationManager from "./DeclarationManager.vue";
import VATDeclarationDetailCardAssist from "./VATDeclarationDetailCardAssist.vue";
import VATDeclarationDetailCardAuto from "./VATDeclarationDetailCardAuto.vue";
import VATDeclarationSummaryCard from "./VATDeclarationSummaryCard.vue";
import { Component, Vue, toNative, Prop, Watch, Hook } from "vue-facing-decorator";
import {
    isGrossAmountExceededVatAssist,
    deleteSubOrAddedAssistItem,
    storeVatItems,
    buildAssistVatItems,
    buildAutoVatItems,
    saveAssistOrAutoVat,
} from "@/services/declaration/declarationServices";
import {
    VatItemAssist,
    VatItemAuto,
} from "../../../../../backend/src/common/declaration/declarationServices";
import { PreviousVatCredit } from "../../../../../backend/src/common/types/taxformTypes";
import VAT_DECLARATION_DONE from "@/graphql/vatDeclaration/VATDeclarationDone.gql";

@Component({
    components: {
        DeclarationForm,
        DeclarationManager,
        VATDeclarationDetailCardAssist,
        VATDeclarationDetailCardAuto,
        VATDeclarationSummaryCard,
    },
})
export class VATDeclarationAssist extends Vue {
    @Prop({ default: null }) companyId: string;
    @Prop({ default: null }) startDate: string;
    @Prop({ default: null }) endDate: string;
    @Prop({ default: "" }) cerfaNumber: string;
    @Prop({ default: "old" }) version: string;
    @Prop({ default: null }) declarationId: string;

    globalStep = 0;

    showSummaryPanel: boolean = true;

    needSave: boolean = false;

    declarationIdAfterSave: string = null;

    confirmVatItemsAlreadyDeclaredDialog: boolean = false;

    pendingAction: string = null;

    get accountingPeriodId() {
        if (this.$store?.state?.accountingPeriod?.selectedAccountingPeriod?.id) {
            return this.$store.state.accountingPeriod.selectedAccountingPeriod.id;
        }
        return null;
    }

    get loading(): boolean {
        return this.loadingAssist || this.loadingAuto;
    }

    get loadingAssist() {
        if (this.$apollo.queries.assistVatItems) {
            return this.$apollo.queries.assistVatItems.loading;
        }
        return false;
    }

    get loadingAuto() {
        if (this.$apollo.queries.autoVatItems) {
            return this.$apollo.queries.autoVatItems.loading;
        }
        return false;
    }

    get noVat(): boolean {
        const vatItemsAutoToAdd: VatItemAuto[] = this.autoVatItemsNotAlreadyDeclared.filter(
            (v) => v.toAdd,
        );
        const vatItemsAssistToAdd: VatItemAssist[] = this.assistVatItemsNotAlreadyDeclared.filter(
            (v) => v.toAdd,
        );
        if (!vatItemsAutoToAdd.length && !vatItemsAssistToAdd.length && !this.previousVatCredit) {
            return true;
        }
        return false;
    }

    get isGrossAmountExceeded(): boolean {
        return isGrossAmountExceededVatAssist(this.assistVatItemsNotAlreadyDeclared);
    }

    get nextIsDisabled() {
        return this.isGrossAmountExceeded || this.noVat;
    }

    get autoVatItemsAlreadyDeclared(): VatItemAuto[] {
        return this.autoVatItems?.filter((item) => item.isAlreadyDeclared);
    }

    get assistVatItemsAlreadyDeclared(): VatItemAssist[] {
        return this.assistVatItems?.filter((item) => item.isAlreadyDeclared);
    }

    get autoVatItemsNotAlreadyDeclared(): VatItemAuto[] {
        return this.autoVatItems?.filter((item) => !item.isAlreadyDeclared);
    }

    get assistVatItemsNotAlreadyDeclared(): VatItemAssist[] {
        return this.assistVatItems?.filter((item) => !item.isAlreadyDeclared);
    }

    get documentAlreadyDeclaredIds(): string[] {
        return this.autoVatItemsAlreadyDeclared
            ?.filter((item) => item.originalType === "document")
            .map((item) => item.docId);
    }

    get bankTransactionAlreadyDeclaredIds(): string[] {
        return [
            ...(this.autoVatItemsAlreadyDeclared
                ?.filter((item) => item.originalType === "bankTransaction")
                .map((item) => item.docId) ?? []),
            ...(this.assistVatItemsAlreadyDeclared?.map((item) => item.bankTransactionId) ?? []),
        ];
    }

    get hasAlreadyDeclaredItems(): boolean {
        const hasElements: boolean =
            this.autoVatItemsAlreadyDeclared?.length > 0 ||
            this.assistVatItemsAlreadyDeclared?.length > 0;
        if (!hasElements) {
            this.confirmVatItemsAlreadyDeclaredDialog = false;
        }
        return hasElements;
    }

    @Watch("assistVatItems", { deep: true })
    @Watch("autoVatItems", { deep: true })
    onVatItemsUpdate() {
        this.needSave = true;
    }

    @Hook
    beforeRouteLeave(to, from, next) {
        if (this.hasAlreadyDeclaredItems && !this.declarationId) {
            this.confirmVatItemsAlreadyDeclaredDialog = true;
        } else {
            next();
        }
    }

    @Apollo({
        query: VAT_CALCULATION_ASSIST,
        variables() {
            return {
                companyId: this.companyId,
                startDate: this.startDate,
                endDate: this.endDate,
                accountingPeriodId: this.accountingPeriodId,
                notReconciliated: false,
            };
        },
        skip() {
            return (
                !this.companyId ||
                !this.startDate ||
                !this.endDate ||
                !this.accountingPeriodId ||
                this.declarationId
            );
        },
        fetchPolicy: "network-only",
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (data) {
                    this.assistVatItems = [];
                    const bankTransactions = data.vatCalculationAssist?.result?.bankTransactions;
                    if (bankTransactions?.length) {
                        buildAssistVatItems("", bankTransactions, this.assistVatItems);
                    }
                    if (data?.vatCalculationAssist?.result?.previousVatCredit) {
                        this.previousVatCredit = data.vatCalculationAssist.result.previousVatCredit;
                    }
                }
            }
        },
    })
    assistVatItems: VatItemAssist[] = [];

    previousVatCredit: PreviousVatCredit = null;

    @Apollo({
        query() {
            return COMPUTE_VAT_DECLARATION_AUTO_IN_ASSIST;
        },
        variables() {
            return {
                companyId: this.companyId,
                endDate: this.endDate,
                accountingPeriodId: this.accountingPeriodId,
            };
        },
        fetchPolicy: "network-only",
        skip() {
            return !this.companyId || !this.accountingPeriodId || this.declarationId;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (data) {
                    this.autoVatItems = [];
                    const documents =
                        data.computeVatDeclarationAutoInAssist?.result?.vatItems?.analysedDocuments;
                    if (documents?.length) {
                        buildAutoVatItems("", documents, this.autoVatItems);
                    }
                }
            }
        },
    })
    autoVatItems: VatItemAuto[] = [];

    back() {
        if (this.hasAlreadyDeclaredItems && !this.declarationId) {
            this.confirmVatItemsAlreadyDeclaredDialog = true;
            this.pendingAction = "backToTaxForms";
        } else {
            this.$router.push({ name: "taxForms" });
        }
    }

    deleteItem(itemToDelete: VatItemAssist) {
        this.assistVatItems = deleteSubOrAddedAssistItem(this.assistVatItems, itemToDelete);
    }

    beforeMount() {
        if (this.declarationId) {
            const storedAssistVatItems: VatItemAssist[] = this.$store?.state?.assistVatItems || [];
            buildAssistVatItems(this.declarationId, storedAssistVatItems, this.assistVatItems);
            const storedAutoVatItems: VatItemAuto[] = this.$store?.state?.autoVatItems || [];
            buildAutoVatItems(this.declarationId, storedAutoVatItems, this.autoVatItems);
            this.previousVatCredit = this.$store?.state?.previousVatCredit || null;
        }
    }

    nextPage() {
        if (this.hasAlreadyDeclaredItems) {
            this.confirmVatItemsAlreadyDeclaredDialog = true;
            this.pendingAction = "nextPage";
        } else {
            this.globalStep++;
        }
    }

    addItem(newItem: VatItemAssist) {
        this.assistVatItems.push(newItem);
    }

    async save() {
        this.declarationIdAfterSave = await saveAssistOrAutoVat(
            this.endDate,
            this.companyId,
            this.accountingPeriodId,
            this.declarationId,
            this.cerfaNumber,
            this.startDate,
            this.assistVatItems,
            this.autoVatItems,
            this.previousVatCredit,
            true,
            false,
        );
        // replace route after save if new assist vat
        if (!this.declarationId) {
            // store vat items to keep after router replace
            storeVatItems(
                this.autoVatItems,
                this.assistVatItems,
                this.previousVatCredit,
                this.$store,
            );
            this.$router.replace({
                name: "vatCalculationAssist",
                params: {
                    startDate: this.startDate,
                    endDate: this.endDate,
                    companyId: this.companyId,
                    cerfaNumber: this.cerfaNumber,
                    version: "new",
                    declarationId: this.declarationIdAfterSave,
                },
            });
        }
        this.needSave = false;
    }

    async vatDeclarationAlreadyDone() {
        await this.$apollo.mutate({
            mutation: VAT_DECLARATION_DONE,
            variables: {
                companyId: this.companyId,
                documents: this.documentAlreadyDeclaredIds,
                bankTransactions: this.bankTransactionAlreadyDeclaredIds,
            },
        });
        this.autoVatItems = this.autoVatItems.filter((item) => !item.isAlreadyDeclared);
        this.assistVatItems = this.assistVatItems.filter((item) => !item.isAlreadyDeclared);
        if (this.declarationId) {
            // store new version of items to use later in this component if page refreshed by user
            storeVatItems(
                this.autoVatItems,
                this.assistVatItems,
                this.previousVatCredit,
                this.$store,
            );
            // save new version of items in database
            await this.save();
        }
        this.confirmVatItemsAlreadyDeclaredDialog = false;

        if (this.pendingAction === "nextPage") {
            this.globalStep++;
        } else if (this.pendingAction === "backToTaxForms") {
            this.$router.push({ name: "taxForms" });
        }
        this.pendingAction = null;
    }
}

export default toNative(VATDeclarationAssist);
</script>
